import { Logger } from 'wdc-cube';
import { averageTexts, NO_MEASUREMENT, METRIC_LABELS, METRIC_TYPES, formatInteger, formatNumber, formatTemperatureVariation, temperatureBaseline, temperatureBaselineWindow, ageOn, buildNightBars, formatDuration, heartRateZoneReferences, nightAverages, trainingSpans } from '../../domain';
import { PeriodSelectorPresenter } from '../period';
import { PatientAnalyticsApi } from './patient-analytics.api';
import { PATIENT_SHORTCUTS } from './patient-analytics.periods';
import { buildSpark } from './spark';
import { AverageScope, GoalScope, MetricOptionScope, MetricSelectorScope, PatientAnalyticsScope, SeriesPointScope } from './patient-analytics.scope';
const LOG = Logger.get('Mormaii.PatientAnalyticsPresenter');
// @Inject
const api = PatientAnalyticsApi.INSTANCE;
/** A legenda das medias de leitura de UMA pessoa - cada dia pesa igual. */
const PER_DAY = 'Média por dia';
/**
 * O painel de analytics de um paciente, montado fora de qualquer presenter.
 *
 * NAO e um `CubePresenter`: nao e um place, e sim um pedaco de tela que dois places
 * hospedam - o detalhe do colaborador e o historico compartilhado. Quem e dono dos
 * parametros e o place que hospeda, porque e o endereco dele que os carrega; este
 * objeto so sabe montar o scope a partir deles.
 *
 * E um PRESENTER sem place, e nao um "painel": painel e a visualizacao, e isto
 * aqui e quem a controla. Sem place de proposito - um place exigiria endereco
 * proprio, o que daria duas URLs para a mesma tela, uma por hospedeiro.
 *
 * Nao estende `CubePresenter`, que e o presenter DE place, nem o `Presenter` do
 * framework - e escolha: recebendo o `update` do hospedeiro, o scope daqui anda no
 * mesmo ciclo de atualizacao da tela, e o tempo de vida e o do campo que guarda
 * este objeto. Nao ha o que liberar.
 */
export class PatientAnalyticsPresenter {
    /**
     * @param update o `update` do presenter hospedeiro, para o scope avisar a view
     * @param onChange chamado quando o leitor mexe num filtro; o hospedeiro escreve
     *                 o endereco, porque os parametros sao dele
     */
    constructor(update, onChange, 
    /**
     * Se o periodo escolhido aqui vira o padrao do painel (B-35). O painel do
     * colaborador lembra; o historico COMPARTILHADO, que e a pagina publica de quem
     * recebeu um link, nao.
     */
    remember = false) {
        this.update = update;
        this.onChange = onChange;
        this.remember = remember;
        this.scope = new PatientAnalyticsScope();
        this.at = { period: '7d', from: '', to: '', metric: 'FREQUENCIA_CARDIACA' };
        /**
         * O seletor de periodo.
         *
         * Sem teto: a ficha le agregados de UMA pessoa, e um trimestre dela e uma
         * consulta pequena. O que ele devolve sobe pelo `onChange` como qualquer outro
         * filtro - o parametro e do place que hospeda, e nao deste pedaco de tela.
         */
        this.period = new PeriodSelectorPresenter(this.update, PATIENT_SHORTCUTS, async ({ from, to, shortcut }) => {
            await this.onChange({ period: shortcut, from, to });
        }, undefined, this.remember);
        /** As seis series da consulta em vigor, por metrica - alimentam as mini-series. */
        this.series = new Map();
        /** Qual requisicao ainda pode falar - duas respostas podem cruzar. */
        this.pending = 0;
        this.referenceToken = 0;
        this.scope.update = update;
        this.scope.onToggleExpanded = async () => {
            this.scope.expanded = !this.scope.expanded;
            await this.showReferences();
        };
    }
    /** A meta de sono da pessoa, em segundos - a que ela escolheu no app (B-41). */
    setSleepGoal(seconds) {
        this.sleepGoal = seconds;
    }
    /** O resumo do periodo: como no app Saude, tempo dormindo e na cama, e a meta. */
    sleepSummaryOf(bars) {
        const { asleepSeconds, inBedSeconds } = nightAverages(bars);
        return [
            ...(asleepSeconds !== undefined ? [`Dormindo ${formatDuration(asleepSeconds)} por noite`] : []),
            ...(inBedSeconds !== undefined ? [`Na cama ${formatDuration(inBedSeconds)} por noite`] : []),
            ...(this.sleepGoal ? [`Meta ${formatDuration(this.sleepGoal)}`] : [])
        ];
    }
    /** A data de nascimento da pessoa - a idade decide as zonas de FC (B-43). */
    setBirthDate(birthDate) {
        this.birthDate = birthDate;
    }
    /**
     * As zonas de frequencia cardiaca por idade, no fundo do grafico ampliado, SO nos
     * trechos de treino (decidido em 14/10/2026). O treino e onde ha frequencia cardiaca
     * de ATIVIDADE (tipo 205), que o app so grava durante um treino.
     *
     * Sem data de nascimento nao ha idade, e sem idade nao ha zona: nada se inventa.
     */
    async showReferences() {
        const target = this.at;
        const age = ageOn(this.birthDate);
        const token = ++this.referenceToken;
        if (!this.scope.expanded || target.metric !== 'FREQUENCIA_CARDIACA' || age === undefined || !this.source) {
            this.scope.references = [];
            return;
        }
        try {
            const query = { startDate: target.from, endDate: target.to };
            const points = await api.fetchTimeSeriesOf(this.source, 'FREQUENCIA_CARDIACA_ATIVIDADE', query);
            if (token !== this.referenceToken) {
                return;
            }
            const step = target.from === target.to ? 60_000 : 3_600_000;
            const spans = trainingSpans(points.map((point) => timeOf(point.date)), step);
            this.scope.references = heartRateZoneReferences(age, spans);
        }
        catch (caught) {
            LOG.warn('Could not load the training spans', caught);
            if (token === this.referenceToken) {
                this.scope.references = [];
            }
        }
    }
    /** Mostra `target` para `source`, buscando o que faltar. */
    /** Ver `answered`. Indefinido antes da primeira resposta, ou se ela nao disse. */
    get answeredPeriod() {
        return this.answered;
    }
    async moveTo(source, target) {
        this.source = source;
        this.showRequest(target);
        const query = { startDate: target.from, endDate: target.to };
        const signature = JSON.stringify([source, query, target.metric]);
        if (signature === this.lastQuery) {
            this.at = target;
            await this.showReferences();
            return;
        }
        const token = ++this.pending;
        this.scope.loading = true;
        this.scope.error = undefined;
        try {
            /*
             * As SEIS series de uma vez, e nao so a selecionada.
             *
             * Cada cartao tem uma mini-serie ao lado do numero, e o grafico grande
             * usa uma delas - buscar so a escolhida deixaria os seis cartoes sem
             * linha e ainda custaria uma requisicao por troca de metrica. E o que o
             * `loadAllMiniCharts` da referencia faz.
             */
            const [analytics, byMetric, , nights] = await Promise.all([
                api.fetchAnalyticsOf(source, query),
                this.fetchEverySeries(source, query),
                // O normal de temperatura (B-45) chega junto, e guarda-se sozinho.
                this.loadBaseline(source),
                /*
                 * As noites com estagios, so quando o sono esta escolhido.
                 *
                 * E outro endpoint (`/metrics/SONO`), e a serie temporal do sono
                 * continua sendo buscada junto das outras cinco - ela e que alimenta
                 * a mini-serie do cartao "Sono". Falhar aqui nao derruba o painel.
                 */
                target.metric === 'SONO'
                    ? api.fetchSleepStagesOf(source, query).catch((caught) => {
                        LOG.warn('Could not load the sleep stages', caught);
                        return [];
                    })
                    : Promise.resolve([])
            ]);
            const series = byMetric.get(target.metric) ?? [];
            if (token !== this.pending) {
                return;
            }
            this.lastQuery = signature;
            this.at = target;
            this.series = byMetric;
            this.showAnalytics(analytics);
            /*
             * O SONO tem dois desenhos (B-41, B-39): num dia so, o hipnograma da noite; num
             * periodo, uma barra por noite, do deitar ao acordar, como no app Saude da
             * Apple - e o detalhe de cada noite aparece sem clique.
             */
            const sleep = target.metric === 'SONO';
            const oneDay = target.from === target.to;
            this.scope.sleepStages = sleep && oneDay;
            this.scope.nightBars = sleep && !oneDay ? buildNightBars(nights) : [];
            this.scope.sleepSummary = sleep && !oneDay ? this.sleepSummaryOf(this.scope.nightBars) : [];
            this.scope.series = sleep
                ? oneDay
                    ? this.buildSleepStageSeries(nights)
                    : []
                : this.buildSeries(series, target.metric);
            await this.showReferences();
        }
        catch (caught) {
            if (token !== this.pending) {
                return;
            }
            LOG.warn('Could not load the patient analytics', caught);
            this.scope.error = caught instanceof Error ? caught.message : 'Não foi possível carregar o painel.';
            this.showRequest(this.at);
        }
        finally {
            if (token === this.pending) {
                this.scope.loading = false;
            }
        }
    }
    /**
     * O normal de temperatura desta pessoa, quando ainda nao esta em maos para hoje.
     *
     * Falhar aqui nao derruba o painel: sem normal, a temperatura fica sobre 36 °C,
     * como era.
     */
    async loadBaseline(source) {
        const window = temperatureBaselineWindow();
        const signature = JSON.stringify([source, window.endDate]);
        if (signature === this.baselineFor) {
            return;
        }
        try {
            const points = await api.fetchTimeSeriesOf(source, 'TEMPERATURA', window);
            this.baseline = temperatureBaseline(points.map((point) => point.averageValue ?? NaN));
            this.baselineFor = signature;
        }
        catch (caught) {
            LOG.warn('Could not load the temperature baseline', caught);
            this.baseline = undefined;
        }
    }
    /**
     * As seis metricas, buscadas juntas.
     *
     * Uma que falha nao derruba as outras: ela fica de fora do mapa, e o cartao dela
     * aparece sem a linha em vez de a tela inteira virar erro.
     */
    async fetchEverySeries(source, query) {
        const results = await Promise.all(METRIC_TYPES.map(async (metric) => {
            try {
                return [metric, await api.fetchTimeSeriesOf(source, metric, query)];
            }
            catch (caught) {
                LOG.warn(`Could not load the ${metric} series`, caught);
                return [metric, undefined];
            }
        }));
        const byMetric = new Map();
        for (const [metric, points] of results) {
            if (points) {
                byMetric.set(metric, points);
            }
        }
        return byMetric;
    }
    showRequest(target) {
        this.scope.metric = target.metric;
        // O seletor calcula o intervalo e move o proprio scope.
        this.period.show(target.period, target.from, target.to);
        this.scope.period = this.period.scope;
        this.buildSelectors(target);
    }
    showAnalytics(analytics) {
        const averages = analytics.healthMetricsAverage;
        const index = analytics.healthIndex;
        this.scope.periodDescription = analytics.periodInfo?.periodDescription ?? '';
        this.answered =
            analytics.periodInfo?.startDate && analytics.periodInfo?.endDate
                ? { from: analytics.periodInfo.startDate.slice(0, 10), to: analytics.periodInfo.endDate.slice(0, 10) }
                : undefined;
        // Os SEIS cartoes da referencia, com os rotulos, os valores e os icones dela.
        // Sem medicao no periodo a media vem `undefined` - ver `averageTexts`.
        const texts = averageTexts(averages);
        this.scope.averages = [
            // As medias de leitura de UMA pessoa sao a media dos dias dela (decisao de
            // 08/10/2026: o cartao diz como foi feito). O sono e por noite, como o grafico.
            this.buildAverage('SONO', texts.SONO, 'moon'),
            this.buildAverage('PRESSAO', texts.PRESSAO, 'activity', PER_DAY),
            this.buildAverage('OXIGENACAO', texts.OXIGENACAO, 'droplet', PER_DAY),
            this.buildAverage('FREQUENCIA_CARDIACA', texts.FREQUENCIA_CARDIACA, 'heart', PER_DAY),
            this.buildAverage('HRV', texts.HRV, 'waves', PER_DAY),
            // A metrica se chama "Temperatura Corporal" e o valor e a VARIACAO (B-03),
            // sobre o NORMAL desta pessoa quando ha um (B-45), e sobre 36 °C quando nao.
            this.buildAverage('TEMPERATURA', this.baseline === undefined || averages?.temperatureAverage == null
                ? texts.TEMPERATURA
                : formatTemperatureVariation(averages.temperatureAverage - this.baseline), 'thermometer', this.baseline === undefined
                ? `${PER_DAY}, sobre 36 °C`
                : `${PER_DAY}, sobre o seu normal (${formatNumber(this.baseline, 1)} °C)`)
        ];
        // As METAS sao DIARIAS e os totais sao do PERIODO: comparar os dois crus
        // diria que quem anda 7.000 passos por dia sete dias seguidos bateu 700% da
        // meta. O painel antigo divide o total pelos dias do periodo antes de
        // comparar, e e o que se faz aqui.
        //
        // Os `*Percentage` que o backend manda NAO servem: vem 100.0 fixo.
        const days = Math.max(1, analytics.periodInfo?.totalDays ?? 1);
        /*
         * As tres metas, com os numeros EXATAMENTE como a referencia os escreve.
         *
         * Cada uma formata de um jeito, e nao ha regra comum: passos vao com
         * separador de milhar e arredondados; calorias vao cruas e arredondadas, sem
         * unidade; distancia vai com duas casas, e o "km" aparece so na META. Uma
         * regra unica deixaria os tres parecidos entre si e diferentes de la.
         */
        this.scope.goals = [
            this.buildGoal('steps', 'Passos (Média/Dia)', formatInteger(Math.round(index.totalSteps / days)), formatInteger(index.stepsGoal || 0), index.totalSteps / days, index.stepsGoal),
            this.buildGoal('calories', 'Calorias (Média/Dia)', String(Math.round(index.totalCalories / days)), String(index.caloriesGoal || 0), index.totalCalories / days, index.caloriesGoal),
            // `totalDistanceKm` esta em METROS - o nome do campo mente. A meta, essa
            // sim, vem em km.
            this.buildGoal('distance', 'Distância (Média/Dia)', formatNumber(index.totalDistanceKm / 1000 / days, 2), formatNumber(index.distanceGoal || 0, 0, 2), index.totalDistanceKm / 1000 / days, index.distanceGoal)
        ];
    }
    /** Uma meta: a media DIARIA alcancada contra o alvo diario. */
    buildGoal(kind, label, value, goal, daily, target) {
        const scope = new GoalScope();
        scope.identity = label;
        scope.kind = kind;
        scope.label = label;
        scope.value = value;
        scope.goal = goal;
        // O anel para em 100%, como o `Math.min(percentage, 100)` de la.
        scope.percentage = target > 0 ? Math.max(0, Math.min(100, (daily / target) * 100)) : 0;
        scope.update = this.update;
        return scope;
    }
    /** O rotulo sai do `METRIC_LABELS` - um nome por metrica, o mesmo de toda tela. */
    buildAverage(metric, value, icon, subtitle = 'Média') {
        const label = METRIC_LABELS[metric];
        const scope = new AverageScope();
        scope.identity = label;
        scope.label = label;
        // Sem medicao no periodo: o traco, e a mesma frase do grafico.
        scope.value = value ?? '-';
        scope.subtitle = value === undefined ? NO_MEASUREMENT : subtitle;
        scope.icon = icon;
        scope.spark = buildSpark(this.series.get(metric), metric, {
            singleDay: this.at.from === this.at.to,
            maxPoints: 30,
            temperatureReference: this.baseline
        });
        scope.update = this.update;
        return scope;
    }
    /**
     * A serie do grafico - TODOS os pontos, como no painel geral.
     *
     * O painel antigo plota hora a hora (`formatChartData` em
     * `CollaboratorDashboard`); quem agrupa por dia la e so a mini-serie dos
     * cartoes. Num painel de UM paciente a variacao dentro do dia e justamente o
     * que interessa.
     *
     * TEMPERATURA vira delta em relacao a 36 C e PRESSAO vira duas curvas, pelas
     * mesmas razoes do painel da empresa.
     */
    buildSeries(points, metric) {
        const pressure = metric === 'PRESSAO';
        const delta = metric === 'TEMPERATURA';
        return (points ?? []).map((point, position) => {
            const raw = point.averageValue ?? 0;
            const scope = new SeriesPointScope();
            scope.identity = `${point.date}:${position}`;
            scope.time = timeOf(point.date);
            scope.count = point.measurementCount ?? 0;
            if (pressure) {
                scope.high = Math.floor(raw / 1000);
                scope.low = raw % 1000;
                scope.value = scope.high;
            }
            else {
                scope.value = Math.round((delta ? raw - (this.baseline ?? 36) : raw) * 100) / 100;
            }
            scope.update = this.update;
            return scope;
        });
    }
    /**
     * A escada do hipnograma.
     *
     * Cada intervalo de estagio vira DOIS pontos com o mesmo valor - o comeco, e um
     * segundo antes do fim. E isso que faz a linha andar em degraus em vez de
     * interpolar entre um estagio e o seguinte, e e como a referencia monta.
     */
    buildSleepStageSeries(nights) {
        const points = [];
        nights.forEach((night, noite) => {
            for (const [indice, stage] of (night.details ?? []).entries()) {
                for (const [borda, time] of [
                    ['a', timeOf(stage.date)],
                    ['b', timeOf(stage.endDate) - 1000]
                ]) {
                    const scope = new SeriesPointScope();
                    scope.identity = `${noite}:${indice}:${borda}`;
                    scope.time = time;
                    scope.value = stage.value;
                    scope.count = 1;
                    scope.update = this.update;
                    points.push(scope);
                }
            }
        });
        return points;
    }
    buildSelectors(target) {
        this.scope.metricSelector = this.buildSelector('Métrica', target.metric, METRIC_TYPES.map((metric) => ({ label: METRIC_LABELS[metric], value: metric })), (value) => ({ metric: (value ?? 'FREQUENCIA_CARDIACA') }));
    }
    buildSelector(label, current, options, target) {
        const selector = new MetricSelectorScope();
        selector.identity = label;
        selector.label = label;
        selector.summary = options.find((option) => option.value === current)?.label ?? label;
        selector.update = this.update;
        selector.options = options.map((option) => {
            const scope = new MetricOptionScope();
            scope.identity = `${label}:${option.value}`;
            scope.label = option.label;
            scope.value = option.value;
            scope.current = option.value === current;
            scope.onSelect = async () => {
                await this.onChange(target(option.value));
            };
            scope.update = this.update;
            return scope;
        });
        return selector;
    }
}
/**
 * O instante de um ponto, em milissegundos.
 *
 * O backend manda `2026-09-05T13:00:00` SEM fuso, que `new Date` le como hora
 * local - o que se quer. O cuidado e com a forma so-data, que a mesma funcao le
 * como UTC e no Brasil volta um dia; por isso ela ganha um `T00:00:00` antes.
 */
function timeOf(date) {
    if (!date) {
        return 0;
    }
    return new Date(date.includes('T') ? date : `${date}T00:00:00`).getTime();
}
//# sourceMappingURL=patient-analytics.presenter.js.map